// apps/mtd-itsa-web/src/layouts/PortalLayout.tsx
import { useEffect, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router";
import {
    Home01,
    Bank,
    BarChart01,
    Settings01,
    LifeBuoy01,
    File04,
    Menu02,
    X as CloseIcon,
} from "@untitledui/icons";
import {
    Button as AriaButton,
    Dialog,
    DialogTrigger as AriaDialogTrigger,
    Modal as AriaModal,
    ModalOverlay as AriaModalOverlay,
} from "react-aria-components";
import { UntitledLogo } from "@/components/foundations/logo/untitledui-logo";
import { NavList } from "@/components/application/app-navigation/base-components/nav-list";
import { NavAccountCard } from "@/components/application/app-navigation/base-components/nav-account-card";
import { NavItemBase } from "@/components/application/app-navigation/base-components/nav-item";
import { NavItemButton } from "@/components/application/app-navigation/base-components/nav-item-button";
import { cx } from "@/utils/cx";
import type { NavItemType } from "@/components/application/app-navigation/config";
import { useProject } from "@/providers/project-provider";
import { VersionBanner } from "@/components/application/version/version-banner";
import { useAuth } from "@/providers/auth-provider";
import { usePostHog } from 'posthog-js/react';

// Side bar code is written directly here for simplicity, as it's only used in PortalLayout. 

// Define navigation items for the sidebar
const navigationItems: NavItemType[] = [
    {
        label: "Obligations",
        href: "/portal/obligations",
        icon: Home01,
    },
    {
        label: "Create Income Stream",
        href: "/portal/create-income-stream",
        icon: BarChart01,
    },
    {
        label: "View Income",
        href: "/portal/view-income",
        icon: BarChart01,
    },
    {
        label: "View Expenses",
        href: "/portal/view-expenses",
        icon: Bank,
    },
    {
        label: "Taxes",
        href: "/portal/taxes",
        icon: File04,
    },
];

const footerItems: NavItemType[] = [
    {
        label: "Support",
        href: "/portal/support",
        icon: LifeBuoy01,
    },
    {
        label: "Settings",
        href: "/portal/settings",
        icon: Settings01,
    },
];

interface PortalLayoutProps {
    children?: ReactNode;
}

export const PortalLayout = ({ children }: PortalLayoutProps) => {
    const location = useLocation();
    const activeUrl = location.pathname;
    const { currentProjectId, projects } = useProject();
    const { user } = useAuth();
    const posthog = usePostHog();

    // Get current project name
    const currentProject = projects.find(p => p.id === currentProjectId);
    const projectName = currentProject?.name || "No Project";


    // Add this effect to identify the user
    useEffect(() => {
        if (user?.id) {
            posthog.identify(user.id, {
                email: user.email // Optional: adds email to their profile in PostHog
            });
        } else {
            posthog.reset(); // Resets if user logs out
        }
    }, [user, posthog]);


    return (
        <div className="flex h-dvh w-full overflow-hidden bg-primary">
            {/* Desktop Sidebar - Always visible on large screens */}
            <aside className="hidden h-full w-72 flex-col justify-between overflow-y-auto border-r border-secondary bg-primary pt-6 lg:flex">
                <div className="flex flex-col gap-5 px-5">
                    <UntitledLogo className="h-8" />
                </div>

                <NavList activeUrl={activeUrl} items={navigationItems} />

                <div className="mt-auto flex flex-col gap-4 px-4 py-6">
                    <div className="flex flex-col gap-1">
                        {footerItems.map((item) => (
                            <NavItemBase
                                key={item.label}
                                type="link"
                                href={item.href}
                                icon={item.icon}
                                badge={item.badge}
                                current={activeUrl === item.href}
                            >
                                {item.label}
                            </NavItemBase>
                        ))}
                    </div>
                    <NavAccountCard />
                </div>
            </aside>

            {/* Main Content Area */}
            <div className="flex min-w-0 flex-1 flex-col">
                {/* Refresh banner goes here for version*/}
                <VersionBanner pollMs={5 * 60 * 1000} />
                {/* App Bar - Top navigation */}
                <header className="flex h-12 md:h-16 w-full items-center justify-between border-b border-secondary bg-primary px-4 md:px-6">
                    {/* Mobile Menu Button and Project Name */}
                    <div className="flex items-center gap-3 lg:hidden">
                        <AriaDialogTrigger>
                            <AriaButton
                                aria-label="Open navigation menu"
                                className="group flex items-center justify-center rounded-lg bg-primary p-2 text-fg-secondary outline-focus-ring hover:bg-primaryhover hover:text-fg-secondaryhover focus-visible:outline-2 focus-visible:outline-offset-2"
                            >
                                <Menu02 className="size-6 transition duration-200 ease-in-out group-aria-expanded:opacity-0" />
                                <CloseIcon className="absolute size-6 opacity-0 transition duration-200 ease-in-out group-aria-expanded:opacity-100" />
                            </AriaButton>

                            {/* Mobile Sidebar Overlay */}
                            <AriaModalOverlay
                                isDismissable
                                className={({ isEntering, isExiting }) =>
                                    cx(
                                        "fixed inset-0 z-50 cursor-pointer bg-overlay70 pr-16 backdrop-blur-md lg:hidden",
                                        isEntering && "duration-300 ease-in-out animate-in fade-in",
                                        isExiting && "duration-200 ease-in-out animate-out fade-out"
                                    )
                                }
                            >
                                <AriaModal className="w-full cursor-auto will-change-transform">
                                    <Dialog className="outline-none">
                                        {({ close }) => (
                                            <>
                                                <AriaButton
                                                    aria-label="Close navigation menu"
                                                    onPress={close}
                                                    className="fixed top-3 right-2 flex cursor-pointer items-center justify-center rounded-lg p-2 text-fg-white70 outline-focus-ring hover:bg-white10 hover:text-fg-white focus-visible:outline-2 focus-visible:outline-offset-2"
                                                >
                                                    <CloseIcon className="size-6" />
                                                </AriaButton>
                                                <div className="h-dvh outline-hidden focus:outline-hidden">
                                                    <aside className="flex h-full max-w-full flex-col justify-between overflow-auto border-r border-secondary bg-primary pt-4">
                                                        {/* Sidebar Header with Logo and Project Name */}
                                                        <div className="flex flex-col gap-3 px-4">
                                                            <div className="flex items-center justify-between">
                                                                <UntitledLogo className="h-8" />
                                                            </div>
                                                            <div className="flex items-center justify-center py-2">
                                                                <span className="text-sm font-semibold text-fg-primary truncate">
                                                                    {projectName}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <NavList activeUrl={activeUrl} items={navigationItems} onItemClick={close} />

                                                        <div className="mt-auto flex flex-col gap-4 px-2 py-4">
                                                            <div className="flex flex-col gap-1">
                                                                {footerItems.map((item) => (
                                                                    <NavItemBase
                                                                        key={item.label}
                                                                        type="link"
                                                                        href={item.href}
                                                                        icon={item.icon}
                                                                        badge={item.badge}
                                                                        current={activeUrl === item.href}
                                                                        onClick={close}
                                                                    >
                                                                        {item.label}
                                                                    </NavItemBase>
                                                                ))}
                                                            </div>
                                                            <NavAccountCard />
                                                        </div>
                                                    </aside>
                                                </div>
                                            </>
                                        )}
                                    </Dialog>
                                </AriaModal>
                            </AriaModalOverlay>
                        </AriaDialogTrigger>

                        {/* Project Name in Mobile Header - replaces logo */}
                        <h1 className="text-base font-semibold text-fg-primary truncate">
                            {projectName}
                        </h1>
                    </div>

                    {/* Desktop - can add project name here too if desired */}
                    <div className="hidden items-center gap-3 lg:flex">
                        <span className="text-sm font-medium text-fg-secondary">
                            {projectName}
                        </span>
                    </div>

                    {/* Right side actions */}
                    <div className="flex items-center gap-3">
                        <div className="flex gap-0.5">
                            <NavItemButton
                                current={activeUrl === "/portal/settings"}
                                size="md"
                                icon={Settings01}
                                label="Settings"
                                href="/portal/settings"
                                tooltipPlacement="bottom"
                            />
                        </div>
                    </div>
                </header>

                {/* Main Content - This is where portal pages will render */}
                <main className="flex-1 overflow-y-auto overscroll-auto bg-primary p-4 md:p-6">
                    <div className="mx-auto h-full w-full max-w-container">
                        {children || <Outlet />}
                    </div>
                </main>
            </div>
        </div>
    );
};

